import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Text } from 'rebass';
import { Input, Label, Select } from '../Form';
import { useMutation } from '@apollo/client';
import { ContactProfileInput } from '../Profile/types';
import Form from '../Form/Form';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import useNotify from '../../hooks/useNotify';
import { WaterSupplierInput } from './types';
import ContactProfileForm from '../Profile/ContactProfileForm';
import {
  CREATE_WATER_SUPPLIER,
  UPDATE_WATER_SUPPLIER,
} from './WaterSupplierEditModal.queries';

type WaterSupplierEditModalProps = {
  onClose: () => void;
  onSave?: (waterSupplier: WaterSupplierInput) => void;
} & (
  | {
      action: 'add';
      waterSupplier?: Partial<WaterSupplierInput>;
    }
  | {
      action: 'edit';
      waterSupplier?: WaterSupplierInput;
    }
);

const WaterSupplierEditModal: React.FC<WaterSupplierEditModalProps> = ({
  onClose,
  onSave,
  action,
  waterSupplier,
}) => {
  const [waterSupplierInput, setWaterSupplierInput] =
    useState<WaterSupplierInput>(
      waterSupplier
        ? {
            profile: waterSupplier.profile
              ? {
                  type: waterSupplier.profile.type || 'COMPANY',
                  name: waterSupplier.profile.name,
                  phones: waterSupplier.profile.phones.map((phone) => ({
                    subType: phone.subType,
                    value: phone.value,
                    extension: phone.extension,
                  })),
                  emails: waterSupplier.profile.emails.map((email) => ({
                    subType: email.subType,
                    value: email.value,
                  })),
                  contactAddressId: waterSupplier.profile.contactAddressId,
                  billingAddressId: waterSupplier.profile.billingAddressId,
                }
              : {
                  type: 'COMPANY',
                  name: '',
                  phones: [],
                  emails: [],
                },
            user: waterSupplier.user
              ? {
                  email: waterSupplier.user.email,
                  password: waterSupplier.user.password,
                }
              : {
                  email: '',
                  password: '',
                },
            acceptEmail: waterSupplier.acceptEmail ?? false,
            reportName: waterSupplier.reportName ?? 'DEFAULT',
            gracePeriod: waterSupplier.gracePeriod ?? null,
            rpCheck2Type: waterSupplier.rpCheck2Type ?? 'USC10',
          }
        : {
            profile: {
              type: 'COMPANY',
              name: '',
              firstName: '',
              lastName: '',
              phones: [],
              emails: [],
            },
            acceptEmail: false,
            reportName: 'DEFAULT',
            gracePeriod: null,
            rpCheck2Type: 'USC10',
          }
    );

  const { showError, showSuccess } = useNotify();

  const [createWaterSupplier, { loading: createLoading }] = useMutation<
    { createWaterSupplier: WaterSupplierInput },
    { input: WaterSupplierInput }
  >(CREATE_WATER_SUPPLIER, {
    onError: (error) => {
      showError('Error creating water supplier', error);
      console.error('Error creating water supplier', error);
    },
    onCompleted: (data) => {
      showSuccess('Water supplier added');
      if (data.createWaterSupplier && onSave) {
        onSave(data.createWaterSupplier);
      }
      onClose();
    },
  });

  const [updateWaterSupplier, { loading: updateLoading }] = useMutation<
    { updateWaterSupplier: WaterSupplierInput },
    { id: string; input: WaterSupplierInput }
  >(UPDATE_WATER_SUPPLIER, {
    onError: (error) => {
      showError('Error updating water supplier', error);
      console.error('Error updating water supplier', error);
    },
    onCompleted: (data) => {
      showSuccess('Water supplier updated');
      if (data.updateWaterSupplier && onSave) {
        onSave(data.updateWaterSupplier);
      }
      onClose();
    },
  });

  const onSubmit = useCallback(async () => {
    if (action === 'add') {
      await createWaterSupplier({
        variables: { input: waterSupplierInput },
      });
    } else if (action === 'edit') {
      await updateWaterSupplier({
        variables: {
          id: waterSupplier?.profileId as string,
          input: waterSupplierInput,
        },
      });
    }
  }, [
    waterSupplierInput,
    createWaterSupplier,
    waterSupplier?.profileId,
    action,
    updateWaterSupplier,
  ]);

  const handleWaterSupplierObjChange = (e: any) => {
    const { name, type, checked } = e.target;
    let { value } = e.target;
    if (type === 'checkbox') {
      value = checked;
    }
    setWaterSupplierInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleWaterSupplierLoginChange = (e: any) => {
    const { name, value } = e.target;
    setWaterSupplierInput((prevState) => ({
      ...prevState,
      user: {
        ...prevState?.user,
        [name]: value,
      },
    }));
  };

  const onProfileChange = (profile: ContactProfileInput) => {
    setWaterSupplierInput((prevState) => ({
      ...prevState,
      profile: {
        ...prevState.profile,
        ...profile,
      },
    }));
  };

  return (
    <Modal
      title={action === 'add' ? 'Add WaterSupplier' : 'Edit WaterSupplier'}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <ContactProfileForm
            onChange={onProfileChange}
            value={waterSupplierInput.profile}
            show={['name', 'contactAddressId', 'emails', 'phones']}
          />
          <FormRow>
            <FormColumn>
              <Label>RP Check 2 Type</Label>
              <Select
                value={waterSupplierInput.rpCheck2Type}
                name="rpCheck2Type"
                onChange={handleWaterSupplierObjChange}
              >
                <option value="USC10">USC 10</option>
                <option value="VALUE">Value</option>
                <option value="VALUE_BUFFER">Value Buffer</option>
              </Select>
            </FormColumn>
            {/*<FormColumn>*/}
            {/*  <Label htmlFor="acceptEmail">Accept Emails</Label>*/}
            {/*  <Checkbox*/}
            {/*    id="acceptEmail"*/}
            {/*    name="acceptEmail"*/}
            {/*    checked={waterSupplierInput.acceptEmail}*/}
            {/*    onChange={handleWaterSupplierObjChange}*/}
            {/*  />*/}
            {/*</FormColumn>*/}
          </FormRow>
          <Text mt={5} variant="body1">
            {waterSupplier?.user?.email && 'Update User Login'}
            {!waterSupplier?.user?.email && 'Create User Login (Optional)'}
          </Text>
          <FormRow>
            <FormColumn>
              <Label>Email</Label>
              <Input
                width="100%"
                type="email"
                value={waterSupplierInput?.user?.email || ''}
                onChange={handleWaterSupplierLoginChange}
                name="email"
                autoComplete="off"
              />
            </FormColumn>
            <FormColumn>
              <Label>Password</Label>
              <Input
                width="100%"
                type="password"
                value={waterSupplierInput?.user?.password || ''}
                onChange={handleWaterSupplierLoginChange}
                name="password"
                autoComplete="new-password"
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="save"
          onClick={onSubmit}
          disabled={createLoading || updateLoading}
        >
          {action === 'add' ? 'Add' : 'Save'}
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

WaterSupplierEditModal.propTypes = {
  onClose: PropTypes.any,
  waterSupplier: PropTypes.any,
  action: PropTypes.any,
};

export default WaterSupplierEditModal;
